import { useContext, useState } from "react";
import { Box, Collapse, Divider, IconButton, LinearProgress, Paper, Typography, Unstable_Grid2 as Grid } from "@mui/material";
import { ExpandMore, OpenInNew } from "@mui/icons-material";
import { Link } from "react-router-dom";
import { useRecord } from "../../../../api/useRecord";
import { useFields } from "../../../../api/useFields";
import { SessionDetailGroup } from "../../../sessions/components/SessionDetailGroup";
import { SessionHeaderData } from "../../../sessions/components/SessionHeader";
import { fieldsQuerySpecific } from "../../../sessions/components/SessionPreview";
import { formatGroups } from "../../../../utils/formatGroups";
import { LogbookSessionItem, LogbookNoteItem } from "../../types";
import { RecordContext } from "../../../../components/record/Record";
import { EvaluationDetailPanel } from "./EvaluationDetailPanel";
import { NeurofeedbackDetailPanel } from "./NeurofeedbackDetailPanel";
import { NoteDetailPanel } from "./NoteDetailPanel";
import { useSessionNFBorEEGDetailPanel } from "../useSessionNFBorEEGDetailPanel";

type SessionDetailPanelProps = {
	session: LogbookSessionItem;
	notes: Array<LogbookNoteItem>;
	treatmentId: string | number;
};

export const SessionDetailPanel = ({ session, notes, treatmentId }: SessionDetailPanelProps) => {
	const [showSessionDetail, setShowSessionDetail] = useState(false);
	const { isNFBorEEGLoading, evaluationItem, nfbItem } = useSessionNFBorEEGDetailPanel(session);

	const recordContext = useContext(RecordContext);
	const client = recordContext?.item?.client;

	const sessionWithTreatment = { ...session, treatment: { id: treatmentId, client } };
	const initialData = { item: sessionWithTreatment, id: session.id, entity: "sessions" };
	const { data, fetchStatus } = useRecord("sessions", fieldsQuerySpecific, session.id, { initialData });
	const { formatedFields } = useFields("sessions");

	const fetchedItem = data?.item ?? session;
	const sessionLoading = fetchStatus === "fetching" && !data;

	const allGroups = formatGroups(formatedFields, fetchedItem, "detail")?.groups || [];
	const groups = allGroups.map((group) => ({
		...group,
		fields: group.fields.filter((field) => field.field !== "treatment"),
	}));

	return (
		<Box>
			<Typography variant="subtitle1" fontWeight="bold" color="primary.main" sx={{ mb: 2 }}>
				Sessão
			</Typography>
			<Paper
				sx={{
					display: "flex",
					flex: 1,
					alignItems: "flex-start",
					gap: 4,
					flexWrap: "wrap",
					mb: 2,
					mx: 2,
					p: 2,
				}}
				elevation={1}
			>
				<Box sx={{ flex: 1 }}>
					<SessionHeaderData session={fetchedItem} hasNote={notes.length > 0} />
				</Box>
				<Box sx={{ display: "flex", alignItems: "center", ml: "auto", flexDirection: "column", justifyContent: "space-between", alignSelf: "stretch" }}>
					<IconButton
						color="secondary"
						component={Link}
						to={`/sessions/${session.id}/detail`}
						target="_blank"
						size="small"
					>
						<OpenInNew />
					</IconButton>
					<IconButton
						size="small"
						onClick={() => setShowSessionDetail((prev) => !prev)}
						sx={{ transition: "transform 0.2s", transform: showSessionDetail ? "rotate(180deg)" : "rotate(0deg)" }}
					>
						<ExpandMore />
					</IconButton>
				</Box>
			</Paper>
			{(isNFBorEEGLoading || sessionLoading) && <LinearProgress />}
			<Collapse in={showSessionDetail}>
				{!sessionLoading && (
					<Grid container spacing={2} sx={{ px: 2 }}>
						{groups.map((group, index) => (
							<Grid key={index} xs={12} lg={6}>
								<SessionDetailGroup group={group} item={fetchedItem} />
							</Grid>
						))}
					</Grid>
				)}
			</Collapse>
			<Divider sx={{ my: 4 }} />
			{evaluationItem && (
				<>
					<Typography variant="subtitle1" fontWeight="bold" color="primary.main" sx={{ mb: 2 }}>
						Avaliação EEG
					</Typography>
					<EvaluationDetailPanel evaluationItem={evaluationItem} />
				</>
			)}
			{nfbItem && (
				<>
					<Typography variant="subtitle1" fontWeight="bold" color="primary.main" sx={{ mb: 2 }}>
						Dados Neurofeedback Z-Score
					</Typography>
					<NeurofeedbackDetailPanel nfbItem={nfbItem} />
				</>
			)}
			{notes.map((note) => (
				<Box key={note.id}>
					<NoteDetailPanel note={note} treatmentId={treatmentId} />
				</Box>
			))}
		</Box>
	);
};
